import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Target, 
  Lightbulb,
  ArrowRight,
  ArrowLeft,
  X,
  Sparkles,
  TrendingUp,
  Clock,
  Award,
  BookOpen,
  Plus
} from 'lucide-react';
import styles from './InteractiveTutorial.module.css';

interface TutorialStep {
  id: string;
  title: string;
  description: string;
  concept: string;
  icon: React.ReactNode;
  actionExample: string;
  tip: string;
}

const tutorialSteps: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Bem-vindo aos Hábitos Atômicos! 🌱',
    description: 'Prepare-se para descobrir como pequenas ações diárias podem criar grandes transformações. A filosofia é simples: melhorar 1% por dia te deixa 37 vezes melhor em um ano!',
    concept: 'Grandes mudanças vêm de pequenas ações consistentes.',
    icon: <Sparkles size={60} />,
    actionExample: 'Em vez de "ler mais", tente "ler 1 página por dia".',
    tip: 'O foco é no sistema, não na meta. Construa bons hábitos, e as metas virão naturalmente.'
  },
  {
    id: 'atomic',
    title: 'O Poder dos Hábitos Atômicos ⚛️',
    description: 'Cada pequeno hábito é como um "átomo". Sozinho parece insignificante, mas quando acumulado dia após dia, constrói uma força imparável.',
    concept: 'Pequenas melhorias compostas geram resultados extraordinários.',
    icon: <TrendingUp size={60} />,
    actionExample: 'Fazer 10 agachamentos ao acordar é melhor que planejar 2 horas de academia.',
    tip: 'Comece tão pequeno que seja impossível falhar. O momentum virá naturalmente.'
  },
  {
    id: 'obvious',
    title: 'Torne-o Óbvio 👀',
    description: 'O primeiro passo é deixar seu hábito claro e visível. Defina onde e quando vai praticá-lo, criando um "gatilho" automático.',
    concept: 'Hábitos óbvios são hábitos que acontecem.',
    icon: <Lightbulb size={60} />,
    actionExample: '"Depois de tomar café, vou ler 1 página" ou "Ao entrar em casa, vou fazer 5 flexões".',
    tip: 'Use a fórmula: "Depois de [hábito atual], eu vou [novo hábito] em [local]".'
  },
  {
    id: 'easy',
    title: 'Torne-o Fácil ⚡',
    description: 'Elimine qualquer atrito que possa atrapalhar. A regra dos 2 minutos: qualquer hábito pode ser iniciado em menos de 2 minutos.',
    concept: 'Reduza o atrito, aumente o sucesso.',
    icon: <Clock size={60} />,
    actionExample: '"Correr 5km" vira "Amarrar os tênis". "Meditar 20 min" vira "Sentar no local de meditação".',
    tip: 'Comece ridiculamente pequeno. Você pode sempre fazer mais, mas comece garantindo que vai fazer.'
  },
  {
    id: 'satisfying',
    title: 'Torne-o Satisfatório 🏆',
    description: 'Celebre cada pequena vitória! Marcar o hábito como concluído no app libera dopamina e reforça o comportamento.',
    concept: 'O que é recompensado é repetido.',
    icon: <Award size={60} />,
    actionExample: 'Marque o ✓ no app, veja sua sequência crescer, celebre cada dia consecutivo.',
    tip: 'Acompanhe o progresso visualmente. Ver a evolução motiva a continuar.'
  },
  {
    id: 'app-tour',
    title: 'Vamos Começar no Axioma Hábitos! 🚀',
    description: 'Agora que você conhece os princípios, vou te mostrar como usar o app para criar seus primeiros hábitos atômicos.',
    concept: 'A ferramenta certa torna tudo mais fácil.',
    icon: <Plus size={60} />,
    actionExample: 'Clique no botão "+" para criar seu primeiro hábito. Comece pequeno e específico!',
    tip: 'Lembre-se: melhor um hábito pequeno todos os dias do que um grande hábito uma vez por semana.'
  }
];

interface InteractiveTutorialProps {
  isOpen: boolean;
  onComplete: () => void;
  onStartAppTour: () => void;
}

const InteractiveTutorial: React.FC<InteractiveTutorialProps> = ({ 
  isOpen, 
  onComplete,
  onStartAppTour 
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  const currentStepData = tutorialSteps[currentStep];
  const isLastStep = currentStep === tutorialSteps.length - 1;
  const handleNext = () => {
    if (isLastStep) {
      // Na última etapa, iniciar tour do app
      onStartAppTour();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <motion.div
        className={styles.tutorialContainer}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", duration: 0.5 }}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.progressIndicator}>
            <span className={styles.stepCounter}>
              {currentStep + 1} de {tutorialSteps.length}
            </span>
            <div className={styles.progressBar}>
              <motion.div
                className={styles.progressFill}
                initial={{ width: 0 }}
                animate={{ width: `${((currentStep + 1) / tutorialSteps.length) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
          <button
            onClick={handleSkip}
            className={styles.skipButton}
          >
            <X size={20} />
            Pular
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* Icon */}
          <motion.div
            key={currentStep}
            className={styles.iconContainer}
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.6, type: "spring" }}
            style={{ color: '#3B82F6' }}
          >
            {currentStepData.icon}
          </motion.div>

          {/* Title */}
          <motion.h2
            key={`title-${currentStep}`}
            className={styles.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {currentStepData.title}
          </motion.h2>

          {/* Description */}
          <motion.p
            key={`desc-${currentStep}`}
            className={styles.description}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {currentStepData.description}
          </motion.p>

          {/* Concept Card */}
          <motion.div
            className={styles.conceptCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className={styles.conceptHeader}>
              <BookOpen size={20} className={styles.conceptIcon} />
              <span className={styles.conceptLabel}>Conceito-chave</span>
            </div>
            <p className={styles.conceptText}>{currentStepData.concept}</p>
          </motion.div>

          {/* Example */}
          <motion.div
            className={styles.exampleCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div className={styles.exampleHeader}>
              <Target size={20} className={styles.exampleIcon} />
              <span className={styles.exampleLabel}>Exemplo prático</span>
            </div>
            <p className={styles.exampleText}>{currentStepData.actionExample}</p>
          </motion.div>

          {/* Tip */}
          <motion.div
            className={styles.tipCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <div className={styles.tipHeader}>
              <Lightbulb size={20} className={styles.tipIcon} />
              <span className={styles.tipLabel}>Dica de ouro</span>
            </div>
            <p className={styles.tipText}>{currentStepData.tip}</p>
          </motion.div>
        </div>

        {/* Navigation */}
        <div className={styles.navigation}>
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className={`${styles.navButton} ${styles.prevButton}`}
          >
            <ArrowLeft size={20} />
            Anterior
          </button>

          <div className={styles.dots}>
            {tutorialSteps.map((_, index) => (
              <div
                key={index}
                className={`${styles.dot} ${
                  index === currentStep ? styles.dotActive : ''
                }`}
                onClick={() => setCurrentStep(index)}
              />
            ))}
          </div>

          <button
            onClick={handleNext}
            className={`${styles.navButton} ${styles.nextButton}`}
          >
            {isLastStep ? 'Começar!' : 'Próximo'}
            <ArrowRight size={20} />
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default InteractiveTutorial;
